'use client'

import { useState, useCallback, useEffect, useRef } from 'react'

interface UseSearchKeyboardProps {
  itemCount: number
  onSelect: (index: number) => void
  onEscape: () => void
  enabled: boolean
}

export function useSearchKeyboard({
  itemCount,
  onSelect,
  onEscape,
  enabled,
}: UseSearchKeyboardProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const activeIndexRef = useRef<number | null>(null)

  useEffect(() => {
    activeIndexRef.current = activeIndex
  }, [activeIndex])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          setActiveIndex(prev => {
            if (prev === null || prev >= itemCount - 1) {
              return 0
            }
            return prev + 1
          })
          break

        case 'ArrowUp':
          event.preventDefault()
          setActiveIndex(prev => {
            if (prev === null || prev <= 0) {
              return itemCount - 1
            }
            return prev - 1
          })
          break

        case 'Enter':
          event.preventDefault()
          if (activeIndexRef.current !== null) {
            onSelect(activeIndexRef.current)
          }
          break

        case 'Escape':
          event.preventDefault()
          setActiveIndex(null)
          onEscape()
          break

        default:
          break
      }
    },
    [enabled, itemCount, onSelect, onEscape]
  )

  useEffect(() => {
    if (enabled) {
      document.addEventListener('keydown', handleKeyDown)
      return () => {
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [enabled, handleKeyDown])

  const resetActiveIndex = useCallback(() => {
    setActiveIndex(null)
  }, [])

  const setActiveIndexDirectly = useCallback((index: number | null) => {
    setActiveIndex(index)
  }, [])

  return {
    activeIndex,
    resetActiveIndex,
    setActiveIndex: setActiveIndexDirectly,
  }
}

